import { BASE_URL } from 'config';
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  Input,
  Avatar,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
const NewEventCategory = ({ onClose, onSuccess, initialData }) => {
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    image: null,
    id: initialData?.id || null,
    imagePreview: initialData?.image || null,
  });
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const imageInputRef = useRef();

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        description: initialData.description || "",
        image: null,
        id: initialData.id || null,
        imagePreview: initialData.image || null,
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "الاسم مطلوب";
    if (!formData.image && !formData.imagePreview) newErrors.image = "الصورة مطلوبة";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && ["image/png", "image/jpeg", "image/gif", "image/jpg"].includes(file.type)) {
      setFormData((prev) => ({ ...prev, image: file, imagePreview: URL.createObjectURL(file) }));
      setErrors((prev) => ({ ...prev, image: "" }));
    } else {
      setErrors((prev) => ({ ...prev, image: "يجب أن تكون الصورة بصيغة PNG أو JPG أو GIF" }));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null, imagePreview: null }));
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return;
    try {
      const formPayload = new FormData();
      formPayload.append("name", formData.name);
      formPayload.append("description", formData.description);
      if (formData.image) formPayload.append("image", formData.image);
      const token = localStorage.getItem("user-token");
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
      let response;
      if (formData.id) {
        response = await axios.post(
          `${BASE_URL}/event-categories/update/${formData.id}`,
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          BASE_URL + "/event-categories/store",
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "99999";
          },
        });
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        willOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };
  const handleCancel = () => {
    setFormData({
      name: "",
      description: "",
      image: null,
    });
  };
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        mx: "auto",
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 3,
        boxShadow: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: { xs: 2, sm: 4 },
        minHeight: 420,
        color: darkMode ? "white !important" : "inherit",
      }}
    >
      <Typography variant="h5" fontWeight="bold" mb={2} align="center">
        {formData.id ? "تعديل فئة فعالية" : "إضافة فئة فعالية جديدة"}
      </Typography>
      <FormControl fullWidth error={!!errors.name}>
        <TextField
          label="اسم الفئة *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
        />
      </FormControl>

      <FormControl fullWidth>
        <TextField
          label="وصف الفئة (اختياري)"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          minRows={3}
          fullWidth
        />
      </FormControl>
      <FormControl fullWidth error={!!errors.image}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} width="100%">
          <label htmlFor="image-upload" style={{ width: "100%" }}>
            <Input
              type="file"
              inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/gif" }}
              onChange={handleImageChange}
              sx={{ display: "none", color: "text.focus !important" }}
              id="image-upload"
              inputRef={imageInputRef}
            />
            <Button
              variant="outlined"
              component="span"
              color="primary"
              startIcon={<UploadIcon />}
              sx={{ width: "100%", color: "text.main", borderColor: "text.border" }}
            >
              تحميل صورة الفئة
            </Button>
          </label>
          {formData.imagePreview && (
            <Box position="relative" mt={1}>
              <Avatar
                src={
                  formData.imagePreview instanceof File
                    ? URL.createObjectURL(formData.imagePreview)
                    : formData.imagePreview
                }
                sx={{ width: "100%", height: 100, borderRadius: 1, objectFit: "contain" }}
              />
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                sx={{ position: "absolute", top: 0, right: 0, bgcolor: "white" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <FormHelperText>{errors.image}</FormHelperText>
        </Box>
      </FormControl>
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.main">
          * الحقول المطلوبة
        </Typography>

        <Box display="flex" gap={2}>
          <Button variant="outlined" color="primary" onClick={handleCancel} sx={{ px: 4, color: "primary.main", borderColor: "primary.main" }} >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            // disabled={Object.keys(errors).length > 0}
            sx={{ px: 4, color: "white !important", bgcolor: "primary.dark", '&:hover': { bgcolor: "primary.main" } }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NewEventCategory;
